import { addSerialNumberToCsv } from "../lib/csv";
import { formatDateValue } from "../lib/dateFormat";
import DateInput from "../components/DateInput";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../lib/api";

const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });
const pctFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 });
const C_UNDER = "#2563eb";
const C_YET = "#cbd5e1";
const LABEL = { under_production: "Under production", yet_to_start: "Yet to start" };
const CHIP = { under_production: "border-blue-200 bg-blue-50 text-blue-800", yet_to_start: "border-slate-200 bg-slate-50 text-slate-600" };
const csvEscape = (v) => { if (v === null || v === undefined) return ""; const s = String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold" style={{ color: accent || "#0f172a" }}>{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

export default function UnderProductionByBuyerDetail() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [options, setOptions] = useState({ consignees: [], countries: [] });
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [consignee, setConsignee] = useState("");
  const [country, setCountry] = useState("");
  const [bucket, setBucket] = useState("");
  const [search, setSearch] = useState("");
  const summarySeq = useRef(0);
  const detailSeq = useRef(0);

  useEffect(() => {
    api.get("/ceo/filter-options")
      .then((r) => setOptions({ consignees: r.data?.consignees || [], countries: r.data?.countries || [] }))
      .catch(() => setOptions({ consignees: [], countries: [] }));
  }, []);

  const body = useMemo(() => ({ from_date: fromDate, to_date: toDate, consignee, country, bucket }), [fromDate, toDate, consignee, country, bucket]);

  const load = useCallback(async () => {
    const seq = ++summarySeq.current; setLoading(true); setError("");
    try { const r = await api.post("/ai/ceo-chat/under-production-by-buyer", body); if (seq !== summarySeq.current) return; setSummary(r.data); }
    catch (err) { if (seq !== summarySeq.current) return; setError(formatErr(err)); }
    finally { if (seq === summarySeq.current) setLoading(false); }
  }, [body]);
  const loadDetail = useCallback(async () => {
    const seq = ++detailSeq.current; setDetailLoading(true); setDetailError("");
    try { const r = await api.post("/ai/ceo-chat/under-production-by-buyer/detail", { ...body, limit: 2000 }); if (seq !== detailSeq.current) return; setDetail(r.data); }
    catch (err) { if (seq !== detailSeq.current) return; setDetailError(formatErr(err)); }
    finally { if (seq === detailSeq.current) setDetailLoading(false); }
  }, [body]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const t = summary?.totals || {};
  const rows = useMemo(() => summary?.rows || [], [summary]);
  const maxOpen = useMemo(() => Math.max(0, ...rows.map((r) => r.open_orders)), [rows]);

  const filteredDetail = useMemo(() => {
    const list = detail?.rows || []; const q = search.trim().toLowerCase(); if (!q) return list;
    return list.filter((r) => [r.po_no, r.customer, r.buyer_name, r.country, r.processor].join(" ").toLowerCase().includes(q));
  }, [detail, search]);

  const exportCsv = useCallback(() => {
    const head = ["Order No", "Order Date", "Ship By", "Buyer (consignee)", "Buyer Name", "Country", "Plant / Processor", "Status", "First Production", "Last Production", "Production Records", "Ordered Cartons"];
    const lines = [head.join(",")].concat(filteredDetail.map((r) => [r.po_no, r.po_date, r.ship_by_date, r.customer, r.buyer_name, r.country, r.processor, r.bucket_label, r.first_production_date, r.last_production_date, r.production_records, r.ordered_mcs].map(csvEscape).join(",")));
    const url = URL.createObjectURL(new Blob([addSerialNumberToCsv(lines.join("\n"))], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a"); a.href = url; a.download = "under-production-by-buyer.csv"; a.click(); URL.revokeObjectURL(url);
  }, [filteredDetail]);

  const selectCls = "mt-1 block rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800";
  const labelCls = "text-xs font-semibold uppercase tracking-wide text-slate-500";

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1500px] space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="max-w-3xl">
            <h1 className="text-xl font-semibold text-slate-900">Under Production — by Buyer</h1>
            <p className="mt-1 text-sm text-slate-600">
              For each buyer, how many of their confirmed open orders have started production and how many are
              still waiting. An order is under production once a freezing or daily-production record exists for
              it — the same rule as the Confirmed Orders view, so the two always agree.
            </p>
          </div>
          <button type="button" onClick={() => navigate("/ceo-command-center?tab=production")}
            className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Back to Process</button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end gap-3">
            <label className={labelCls}>From Date<DateInput value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={selectCls} /></label>
            <label className={labelCls}>To Date<DateInput value={toDate} onChange={(e) => setToDate(e.target.value)} className={selectCls} /></label>
            <label className={labelCls}>Buyer
              <select value={consignee} onChange={(e) => setConsignee(e.target.value)} className={`${selectCls} max-w-[230px]`}>
                <option value="">All Buyers</option>{options.consignees.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label className={labelCls}>Country
              <select value={country} onChange={(e) => setCountry(e.target.value)} className={`${selectCls} max-w-[180px]`}>
                <option value="">All Countries</option>{options.countries.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
            <label className={labelCls}>Status
              <select value={bucket} onChange={(e) => setBucket(e.target.value)} className={selectCls}>
                <option value="">All Orders</option><option value="under_production">Under production</option><option value="yet_to_start">Yet to start</option></select></label>
            <button type="button" onClick={() => { setFromDate(""); setToDate(""); setConsignee(""); setCountry(""); setBucket(""); }}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Reset Filters</button>
          </div>
          <div className="mt-2 text-xs text-slate-500">
                        {!fromDate && !toDate ? "No date filter applied — every open confirmed order is shown." : ""}{" "}
            The buyer table always shows both sides, so its figures add up even when the Status filter is set.
          </div>
        </div>

        {error ? <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{error}</div> : null}
        {loading ? <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading…</div> : null}

        {!loading && summary ? (<>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <StatTile label="Open Confirmed Orders" value={numFmt.format(t.open_orders || 0)} sub="Confirmed, not yet shipped" />
            <StatTile label="Under Production" value={numFmt.format(t.under_production || 0)} sub="Production has started" accent={C_UNDER} />
            <StatTile label="Yet to Start" value={numFmt.format(t.yet_to_start || 0)} sub="No production record yet" />
            <StatTile label="Share Under Production" value={t.under_production_pct != null ? `${pctFmt.format(t.under_production_pct)}%` : "—"} sub="Of open confirmed orders" />
            <StatTile label="Buyers" value={numFmt.format(t.buyers || 0)} sub={`${numFmt.format(t.buyers_with_production || 0)} with production started`} />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-sm font-semibold text-slate-900">Orders under production, by buyer</div>
            <div className="mt-1 text-xs text-slate-500">Blue = under production · grey = yet to start. Bar length is the buyer's open confirmed orders.</div>
            <div className="mt-4 overflow-x-auto"><div className="min-w-[720px] space-y-2">
              <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <div className="w-56 shrink-0">Buyer</div>
                <div className="flex-1">Open confirmed orders</div>
                <div className="w-24 shrink-0 text-right">Under production</div>
                <div className="w-20 shrink-0 text-right">Yet to start</div>
                <div className="w-20 shrink-0 text-right">Open orders</div>
                <div className="w-16 shrink-0 text-right">Share</div>
              </div>
              {rows.map((r) => (
                <div key={r.customer} className="flex items-center gap-3">
                  <div className="w-56 shrink-0 truncate text-xs text-slate-700" title={r.buyer_name ? `${r.customer} (${r.buyer_name})` : r.customer}>{r.customer}</div>
                  <div className="flex h-3 flex-1 overflow-hidden rounded-full bg-slate-100">
                    <div style={{ width: `${maxOpen ? (r.under_production / maxOpen) * 100 : 0}%`, backgroundColor: C_UNDER }} />
                    <div style={{ width: `${maxOpen ? (r.yet_to_start / maxOpen) * 100 : 0}%`, backgroundColor: C_YET }} />
                  </div>
                  <div className="w-24 shrink-0 text-right text-xs font-semibold" style={{ color: C_UNDER }}>{numFmt.format(r.under_production)}</div>
                  <div className="w-20 shrink-0 text-right text-xs text-slate-600">{numFmt.format(r.yet_to_start)}</div>
                  <div className="w-20 shrink-0 text-right text-xs text-slate-900">{numFmt.format(r.open_orders)}</div>
                  <div className="w-16 shrink-0 text-right text-xs text-slate-500">{r.under_production_pct != null ? `${pctFmt.format(r.under_production_pct)}%` : "—"}</div>
                </div>
              ))}
            </div></div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-900">Orders</div>
                <div className="mt-1 text-xs text-slate-500">
                  {detailLoading ? "Loading…" : detail?.truncated
                    ? `Showing ${numFmt.format(filteredDetail.length)} of ${numFmt.format(detail.matching_orders || 0)} orders — narrow the filters to see them all`
                    : `${numFmt.format(filteredDetail.length)} order(s)`}
                </div>
              </div>
              <div className="flex gap-2">
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search order, buyer, plant" className="rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <button type="button" onClick={exportCsv} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Export CSV</button>
              </div>
            </div>
            {detailLoading ? <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-6 text-center text-sm text-slate-500">Loading orders…</div> : null}
            {detailError ? <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">The order list could not be loaded: {detailError} The figures above are unaffected.</div> : null}
            <div className={`mt-3 overflow-x-auto${detailLoading || detailError ? " hidden" : ""}`}>
              <table className="w-full text-left text-sm">
                <thead><tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="w-px whitespace-nowrap py-2 pr-3 align-bottom">Order No</th><th className="w-px whitespace-nowrap py-2 pr-3 align-bottom">Order Date</th><th className="whitespace-nowrap py-2 pr-3 align-bottom">Buyer</th>
                  <th className="w-px whitespace-nowrap py-2 pr-3 align-bottom">Country</th><th className="w-px whitespace-nowrap py-2 pr-3 align-bottom">Plant</th><th className="w-px whitespace-nowrap py-2 pr-3 align-bottom">Status</th>
                  <th className="w-px whitespace-nowrap py-2 pr-3 align-bottom">Production Started</th><th className="w-px whitespace-nowrap py-2 pr-3 align-bottom text-right">Records</th><th className="w-px whitespace-nowrap py-2 pr-3 align-bottom text-right">Cartons</th>
                </tr></thead>
                <tbody>{filteredDetail.slice(0, 300).map((r, i) => (
                  <tr key={`${r.po_id}-${i}`} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium text-slate-800 whitespace-nowrap">{r.po_no}</td>
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{formatDateValue(r.po_date)}</td>
                    <td className="py-2 pr-3 text-slate-700">{r.customer}{r.buyer_name ? <span className="text-xs text-slate-500"> ({r.buyer_name})</span> : null}</td>
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{r.country}</td>
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{r.processor || "—"}</td>
                    <td className="py-2 pr-3 whitespace-nowrap"><span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs ${CHIP[r.bucket] || ""}`}>{LABEL[r.bucket] || r.bucket}</span></td>
                    <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{formatDateValue(r.first_production_date) || "—"}</td>
                    <td className="py-2 pr-3 text-right text-slate-700">{r.production_records ? numFmt.format(r.production_records) : "—"}</td>
                    <td className="py-2 pr-3 text-right text-slate-800">{numFmt.format(r.ordered_mcs)}</td>
                  </tr>))}</tbody>
              </table>
              {filteredDetail.length > 300 ? <div className="mt-2 text-xs text-slate-500">Showing the first 300 rows on screen. Export CSV includes all {numFmt.format(filteredDetail.length)}.</div> : null}
            </div>
          </div>

        </>) : null}
      </div>
    </div>
  );
}
